import axiosInstance from './axios';
import { API_URL, API_ENDPOINTS } from '../config';

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Handles user login
 * @param {Object} credentials - User credentials
 * @returns {Promise} Response from the server
 */
export const login = async (credentials) => {
  console.log("Login attempt with:", { username: credentials.username, password: '******' });
  console.log("Current domain:", window.location.hostname);
  console.log("API URL being used:", API_URL);
  console.log("Login endpoint:", API_ENDPOINTS.LOGIN);
  
  try {
    console.log("Calling login endpoint:", API_ENDPOINTS.LOGIN);
    const response = await axiosInstance.post(API_ENDPOINTS.LOGIN, credentials);
    
    console.log("Login response status:", response.status);
    console.log("Login response full data:", JSON.stringify(response.data, null, 2));
    
    if (response.data?.requiresUsernameSelection) {
      // Email-first login step: backend returns matching usernames
      return response.data;
    }

    if (response.data && response.data.token) {
      console.log("Token received, storing in localStorage");
      
      // Ensure we clear any previous values first
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userGrade');
      
      // Set new values
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userId', response.data.userId);
      localStorage.setItem('isAuthenticated', 'true');
      
      // Store user grade if available
      if (response.data.grade) {
        localStorage.setItem('userGrade', response.data.grade);
      }
      
      // Set last activity timestamp
      updateLastActivity();
      
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      
      // Verify localStorage was updated
      console.log("LocalStorage after login:", {
        token: localStorage.getItem('token'),
        tokenLength: localStorage.getItem('token') ? localStorage.getItem('token').length : 0,
        userId: localStorage.getItem('userId'),
        isAuthenticated: localStorage.getItem('isAuthenticated')
      });
      
      return response.data;
    } else {
      console.error("Login response missing token:", response.data);
      throw new Error("Invalid response from server - missing token");
    }
  } catch (error) {
    console.error("Login error:", error.message);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", JSON.stringify(error.response.data, null, 2));
      
      // If we got a 403 forbidden response with a message
      if (error.response.status === 403 && error.response.data?.message) {
        console.error("Access denied message:", error.response.data.message);
      }
    } else if (error.request) {
      console.error("No response received");
    }
    throw error;
  }
};

/**
 * Updates the last activity timestamp
 */
export const updateLastActivity = () => {
  localStorage.setItem('lastActivityTime', Date.now().toString());
};

/**
 * Setup activity listeners to track user activity (no auto-logout on inactivity)
 */
export const setupActivityTracking = () => {
  return () => {};
};

/**
 * Handles user signup
 * @param {Object} userData - User registration data
 * @returns {Promise} Response from the server
 */
export const signup = async (userData) => {
  const response = await axiosInstance.post(API_ENDPOINTS.USER_SIGNUP, userData);
  return response.data;
};

export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const setRefreshToken = (token) => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Checks if the user is authenticated
 * @returns {boolean} Whether the user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

export const refreshToken = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axiosInstance.post('/auth/refresh', { refreshToken });
    const { token, refreshToken: newRefreshToken } = response.data;

    setToken(token);
    setRefreshToken(newRefreshToken);

    return token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    removeToken();
    throw error;
  }
};

/**
 * Logs out the user
 */
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('lastActivityTime');
  localStorage.removeItem('userGrade');
  
  // Clear session timeout interval
  if (window._sessionTimeoutInterval) {
    clearInterval(window._sessionTimeoutInterval);
  }
  
  window.location.href = '/login';
};

/**
 * Checks if admin is authenticated
 * @returns {boolean}
 */
export const isAdminAuthenticated = () => {
  const adminToken = localStorage.getItem('adminToken');
  
  if (!adminToken) {
    return false;
  }
  
  // Check if token looks valid (simple check)
  if (adminToken && typeof adminToken === 'string' && adminToken.length > 20) {
    return true;
  }
  
  return false;
};

/**
 * Gets the current user's ID
 * @returns {string|null}
 */
export const getCurrentUserId = () => {
  return localStorage.getItem('userId');
}; 